import type { ReactElement } from 'react';
import { useParams } from 'react-router-dom';
import type { UseQueryResult } from '@tanstack/react-query';

import { UserListModal } from '@/components/UserListModal';
import { useToolUsers } from '@/hooks/api/useShadowAiTools';

import { COLUMNS, type AIToolUser } from './constants';

interface ToolInfo {
  toolId: string;
  name: string;
  riskLevel?: string;
  userCount: number;
}

interface AIToolUserListModalProps {
  open: boolean;
  onClose: () => void;
  tool: ToolInfo | null;
  emptyStateMessage?: string;
}

export function AIToolUserListModal({
  open,
  onClose,
  tool,
  emptyStateMessage = 'No users found for this tool',
}: AIToolUserListModalProps): ReactElement | null {
  const { organizationId } = useParams<{ organizationId: string }>();
  const toolUsersQuery = useToolUsers(
    organizationId,
    tool?.toolId ?? undefined,
    open
  );

  // Transform the data to match our column structure
  const transformedData = toolUsersQuery.data?.users ?? [];
  const query = {
    ...toolUsersQuery,
    data: transformedData,
  } as unknown as UseQueryResult<AIToolUser[]>;

  if (!tool) {
    return null;
  }

  return (
    <UserListModal<AIToolUser>
      open={open}
      onOpenChange={isOpen => {
        if (!isOpen) {
          onClose();
        }
      }}
      title={
        <>
          Users of <span className="font-mono">{tool.name}</span>
        </>
      }
      query={query}
      columns={COLUMNS}
      emptyState={emptyStateMessage}
    />
  );
}
